import { CompoundEntityRef, Entity, parseEntityRef } from '@backstage/catalog-model';

export interface TopDocLink {
  entityRef: string;
  title: string;
  description?: string;
  path: string;
}

// TechDocs links use lower-case triplets (unless the legacy case-sensitive option is enabled).
const docPath = ({ namespace, kind, name }: CompoundEntityRef) =>
  `/docs/${[namespace, kind, name]
    .map(part => encodeURIComponent(part.toLocaleLowerCase('en-US')))
    .join('/')}`;

const tryParse = (ref: string): CompoundEntityRef | undefined => {
  try {
    return parseEntityRef(ref);
  } catch {
    return undefined;
  }
};

/**
 * Turns ranked doc refs into links, in rank order, keeping at most `limit`.
 *
 * With `entities` (the catalog lookup for each ref, `undefined` when missing) titles come from
 * the catalog and docs that no longer exist are dropped. Without it (lookup failed) the entity
 * name is used as the title.
 */
export function toTopDocLinks(
  refs: string[],
  limit: number,
  entities?: Array<Entity | undefined>,
): TopDocLink[] {
  const links: TopDocLink[] = [];
  const seen = new Set<string>();
  refs.forEach((entityRef, index) => {
    const key = entityRef.toLocaleLowerCase('en-US');
    if (links.length >= limit || seen.has(key)) return;
    const parsed = tryParse(entityRef);
    if (!parsed) return;
    if (entities) {
      const entity = entities[index];
      if (!entity) return;
      const compound = {
        kind: entity.kind,
        namespace: entity.metadata.namespace ?? 'default',
        name: entity.metadata.name,
      };
      links.push({
        entityRef,
        title: entity.metadata.title || entity.metadata.name,
        description: entity.metadata.description,
        path: docPath(compound),
      });
    } else {
      links.push({ entityRef, title: parsed.name, path: docPath(parsed) });
    }
    seen.add(key);
  });
  return links;
}
